import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type NotificationType = 'conversation_assignment' | 'download' | 'sync-failed';

export interface NotificationData {
  title: string;
  body: string;
  navigationPath?: string;
  onPress?: () => void;
  type?: NotificationType;
  route?: string;
}

interface NotificationToastContextType {
  showToast: boolean;
  notificationData: NotificationData;
  onShowToast: (data: NotificationData) => void;
  onCloseToast: () => void;
  onToastClick: () => void;
}

const NotificationToastContext =
  createContext<NotificationToastContextType | null>(null);

export const NotificationToastProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showToast, setShowToast] = useState(false);
  const [notificationData, setNotificationData] = useState<NotificationData>({
    title: '',
    body: '',
  });
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const onShowToast = useCallback((data: NotificationData) => {
    setNotificationData(data);
    if (data?.route && data.route !== pathnameRef.current) return;
    setShowToast(true);
  }, []);

  const onCloseToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const onToastClick = useCallback(() => {
    if (notificationData.navigationPath) {
      navigate(notificationData.navigationPath);
    }
    if (notificationData.onPress) {
      notificationData.onPress();
    }
    setShowToast(false);
  }, [notificationData, navigate]);

  return (
    <NotificationToastContext.Provider
      value={{
        showToast,
        notificationData,
        onShowToast,
        onCloseToast,
        onToastClick,
      }}
    >
      {children}
    </NotificationToastContext.Provider>
  );
};

export const useNotificationToast = () => {
  const ctx = useContext(NotificationToastContext);
  if (!ctx) {
    throw new Error(
      'useNotificationToast must be used inside NotificationToastProvider'
    );
  }
  return ctx;
};
